import type { Translate } from "@khazzin/i18n";
import { Clock, Info, TrendingDown, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Sparkline } from "../../charts/Sparkline";
import { Icon } from "../../icons/Icon";
import { Skeleton } from "../../primitives/Skeleton";
import { cx, FOCUS_RING } from "../../styles";
import { ErrorState } from "./states";

/**
 * One number on a dashboard, with its own loading and its own failure.
 *
 * **Each tile loads and fails alone.** `13/01` §3 and §8 both say so, and the
 * reason is what a dashboard is for: six numbers, and the one that cannot be
 * computed must not take the other five with it. A single page-level error over
 * a dashboard is the difference between "the finance figure is unavailable" and
 * "the dashboard is down".
 *
 * **`stale` is a real state, not a variant of ready.** A figure computed from a
 * cache that could not be refreshed is still worth showing — it was true a few
 * minutes ago — but somebody about to act on it has to know that. A clock
 * badge says it without hiding the number.
 */

export type KpiStatus = "loading" | "error" | "ready" | "stale";

export interface KpiDelta {
	/** The change as a percentage. Negative is a fall. */
	pct: number;
	/** What it is compared against: "vs last week". */
	label: string;
	/**
	 * Whether a fall is good news.
	 *
	 * Returns rate, cancellation rate, time-to-pick: down is the goal. Without
	 * this a dashboard paints every improvement red, which teaches people to
	 * stop reading the colour.
	 */
	lowerIsBetter?: boolean;
}

/** How far a figure is towards its target (`Analytics.dc.html#exec`, EP13-S04). */
export interface KpiTarget {
	/** The target as it reads: "$800,000". */
	value: ReactNode;
	/** The figure as a percentage of the target, rounded. */
	pct: number;
}

/** At or above this share of target a tile reads as on track. */
export const TARGET_ON_TRACK_PCT = 95;

export interface KpiTileProps {
	label: string;
	value: ReactNode;
	status: KpiStatus;
	t: Translate;
	delta?: KpiDelta;
	/** A series to draw behind the number. Twelve or so points. */
	sparkline?: number[];
	target?: KpiTarget;
	error?: unknown;
	onRetry?: () => void;
	/** Drills down to the list this number counts. */
	href?: string;
	/** What the number means, from the metric dictionary (`useMetricDefinition`). */
	definition?: string;
	className?: string;
}

export function KpiTile({
	label,
	value,
	status,
	t,
	delta,
	sparkline,
	target,
	error,
	onRetry,
	href,
	definition,
	className,
}: KpiTileProps) {
	const card = cx("flex min-w-0 flex-col gap-2 card p-4", className);

	if (status === "error") {
		return (
			<div className={card}>
				<p className="text-[11.5px] font-bold uppercase tracking-wide text-fg-subtle">{label}</p>
				{/* Compact, so a failed tile stays the size of a tile and the grid
				    does not rearrange around it. */}
				<ErrorState error={error} t={t} compact {...(onRetry === undefined ? {} : { onRetry })} />
			</div>
		);
	}

	const body = (
		<>
			<div className="flex items-center gap-1.5">
				<p className="min-w-0 flex-1 truncate text-[11.5px] font-bold uppercase tracking-wide text-fg-subtle">
					{label}
				</p>
				{definition !== undefined && (
					// The metric dictionary's one line (EP13-S03), on hover and to a
					// screen reader: the same words as the Metric dictionary's.
					<span title={definition} className="inline-flex shrink-0 text-fg-subtle">
						<Icon as={Info} size={13} title={definition} />
					</span>
				)}
				{status === "stale" && (
					// Named, not just drawn: a clock icon alone is a mystery, and this
					// is the one badge somebody has to understand before acting.
					<Icon
						as={Clock}
						size={13}
						className="shrink-0 text-warning-fg"
						title={t("ui.kpi.stale")}
					/>
				)}
			</div>

			{status === "loading" ? (
				<Skeleton className="h-[26px] w-24" />
			) : (
				<p className="text-[26px] font-bold leading-none tracking-[-0.02em] text-fg [font-variant-numeric:tabular-nums]">
					{value}
				</p>
			)}

			<div className="flex items-end justify-between gap-2">
				{delta !== undefined && status !== "loading" && <Delta delta={delta} />}
				{sparkline !== undefined && sparkline.length > 1 && status !== "loading" && (
					<Sparkline points={sparkline} />
				)}
			</div>

			{target !== undefined && status !== "loading" && <Target target={target} t={t} />}
		</>
	);

	if (href === undefined) {
		return <div className={card}>{body}</div>;
	}

	return (
		// A link, not a div with an onClick: a number worth drilling into is worth
		// opening in a second tab, and a keyboard should reach it in one Tab.
		<Link to={href} className={cx(card, "card-interactive no-underline", FOCUS_RING)}>
			{body}
		</Link>
	);
}

function Delta({ delta }: { delta: KpiDelta }) {
	const rising = delta.pct >= 0;
	const good = delta.lowerIsBetter === true ? !rising : rising;

	return (
		<p
			className={cx(
				"flex items-center gap-1 text-[11.5px] font-bold",
				// Flat is neither: a 0% change coloured green is a claim nobody made.
				delta.pct === 0 ? "text-fg-muted" : good ? "text-success-fg" : "text-warning-fg",
			)}
		>
			{delta.pct !== 0 && <Icon as={rising ? TrendingUp : TrendingDown} size={13} />}
			<span className="[font-variant-numeric:tabular-nums]">
				{rising ? "+" : ""}
				{delta.pct}%
			</span>
			<span className="font-medium text-fg-subtle">{delta.label}</span>
		</p>
	);
}

/**
 * The target line of a north-star tile: the target, a chip saying how far the
 * figure is towards it, and a bar of the same. The chip's words carry the
 * reading; the colour only repeats it.
 */
function Target({ target, t }: { target: KpiTarget; t: Translate }) {
	const onTrack = target.pct >= TARGET_ON_TRACK_PCT;
	return (
		<div className="flex flex-col gap-1.5">
			<div className="flex items-center justify-between gap-2 text-[11.5px]">
				<span className="text-fg-subtle">
					{t("ui.kpi.target")} <span className="font-semibold text-fg-muted">{target.value}</span>
				</span>
				<span
					className={cx(
						"rounded-full px-1.5 py-px font-bold",
						onTrack ? "bg-success-bg text-success-fg" : "bg-warning-bg text-warning-fg",
					)}
				>
					{target.pct >= 100 ? t("ui.kpi.onPlan") : t("ui.kpi.ofTarget", { pct: target.pct })}
				</span>
			</div>
			<span
				aria-hidden="true"
				className="relative block h-1 overflow-hidden rounded-full bg-bg-subtle"
			>
				<span
					className={cx(
						"absolute inset-y-0 start-0 block",
						onTrack ? "bg-success-fg" : "bg-warning-fg",
					)}
					style={{ width: `${Math.min(100, Math.max(0, target.pct))}%` }}
				/>
			</span>
		</div>
	);
}
